/**
 * @fileoverview 市场筛选栏组件
 * @description 提供市场分类、状态筛选、关键词搜索和排序功能
 * 移动端适配：分类标签横向滚动，搜索/排序行响应式布局
 * @module components/market/MarketFilter
 */

import { ChevronDown, Search, SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMarketStore } from '../../stores/useMarketStore';
import { cn } from '../../lib/utils';
import type { MarketCategory } from '../../types';

// ============================================================
// 分类配置
// ============================================================

const CATEGORY_ICON_URLS: Record<string, string> = {
  all:       '/icons/cat-all.webp',
  high_freq: '/icons/cat-high-freq.webp',
  crypto:    '/icons/cat-crypto.png',
  commodity: '/icons/cat-commodity.png',
  forex:     '/icons/cat-forex.webp',
  stock:     '/icons/cat-stock.webp',
  index:     '/icons/cat-index.webp',
  event:     '/icons/cat-event.webp',
};

/** 市场分类列表（与服务器 MARKET_GROUP_TYPE 保持一致） */
const CATEGORIES: { key: MarketCategory; labelKey: string; label: string }[] = [
  { key: 'all',       labelKey: 'market.category.all',       label: '全部'     },
  { key: 'high_freq', labelKey: 'market.category.high_freq', label: '高頻'     },
  { key: 'crypto',    labelKey: 'market.category.crypto',    label: '加密貨幣' },
  { key: 'commodity', labelKey: 'market.category.commodity', label: '大宗商品' },
  { key: 'forex',     labelKey: 'market.category.forex',     label: '外匯'     },
  { key: 'stock',     labelKey: 'market.category.stock',     label: '股票'     },
  { key: 'index',     labelKey: 'market.category.index',     label: '指數'     },
  { key: 'event',     labelKey: 'market.category.event',     label: '事件'     },
];

/** 排序选项列表 */
const SORT_OPTIONS = [
  { key: 'volume', labelKey: 'market.sort.volume' },
  { key: 'endTime', labelKey: 'market.sort.endTime' },
  { key: 'newest', labelKey: 'market.sort.newest' },
  { key: 'liquidity', labelKey: 'market.sort.liquidity' },
] as const;

// ============================================================
// 主组件：市场筛选栏
// ============================================================

/**
 * 市场筛选栏主组件
 * 包含分类标签（横向滚动）、搜索框、排序下拉
 * 移动端：分类标签行可横向滚动，搜索+排序行自适应
 */
export default function MarketFilter() {
  const { t } = useTranslation();
  const { filters, setFilters } = useMarketStore();

  const handleCategoryChange = (category: MarketCategory) => {
    setFilters({ category });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ keyword: e.target.value });
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters({ sortBy: e.target.value as typeof filters.sortBy });
  };

  return (
    <div className="space-y-3">
      {/* 分类标签行：横向滚动，隐藏滚动条 */}
      <div
        className="flex items-center gap-2 overflow-x-auto scrollbar-hide"
        style={{ WebkitOverflowScrolling: 'touch', paddingBottom: '2px' }}
      >
        {CATEGORIES.map((cat) => (
          <button
            key={cat.key}
            onClick={() => handleCategoryChange(cat.key)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all shrink-0',
              filters.category === cat.key
                ? 'bg-brand text-[#0B0E11] font-bold'
                : 'bg-white/6 text-muted-foreground hover:text-foreground hover:bg-white/10 border border-white/8'
            )}
          >
            <img
              src={CATEGORY_ICON_URLS[cat.key]}
              alt={cat.label}
              className="w-4 h-4 object-contain shrink-0"
              style={{ filter: 'none' }}
            />
            <span className="text-xs sm:text-sm">{t(cat.labelKey)}</span>
          </button>
        ))}
      </div>

      {/* 搜索框 + 排序：移动端全宽堆叠，桌面端同行 */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        {/* 搜索框 */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={filters.keyword}
            onChange={handleSearchChange}
            placeholder={t('common.search') + '...'}
            className="w-full bg-white/5 border border-white/8 rounded-xl pl-9 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-brand/50 focus:bg-white/8 transition-all"
          />
        </div>

        {/* 排序下拉 */}
        <div className="relative shrink-0">
          <SlidersHorizontal className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <select
            value={filters.sortBy}
            onChange={handleSortChange}
            className="w-full sm:w-auto bg-white/5 border border-white/8 rounded-xl pl-9 pr-9 py-2.5 text-sm text-foreground focus:outline-none focus:border-brand/50 transition-all cursor-pointer appearance-none"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.key} value={opt.key} className="bg-[#1E2026] text-foreground">
                {t(opt.labelKey)}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
